import { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Badge,
  Modal,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useWebhooks,
  useCreateWebhook,
  useDeleteWebhook,
  useWebhookDeliveries,
  type Webhook,
  type WebhookStatus,
  type Delivery,
} from './useIntegration';

/* ── Helpers ── */

/* Tres estados do dominio (`WebhookSubscriptionStatus`). Pausado e desativado
   nao sao a mesma coisa: um e interrupcao temporaria, o outro e fim da
   assinatura. */
const WEBHOOK_STATUS_LABEL: Record<WebhookStatus, string> = {
  ACTIVE: 'Ativo',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
};

const WEBHOOK_STATUS_VARIANT: Record<WebhookStatus, 'success' | 'warning' | 'neutral'> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  DISABLED: 'neutral',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const AVAILABLE_EVENTS = [
  'sales.order.created',
  'sales.order.confirmed',
  'sales.order.cancelled',
  'sales.order.invoiced',
  'sales.order.completed',
  'sales.quote.accepted',
  'sales.quote.rejected',
  'sales.quote.converted',
  'support.ticket.created',
  'support.ticket.message-added',
  'support.ticket.closed',
  'support.ticket.resolved',
  'order.created',
  'order.updated',
  'order.cancelled',
  'invoice.emitted',
  'invoice.cancelled',
  'payment.received',
  'payment.confirmed',
  'product.created',
  'product.updated',
  'customer.created',
  'customer.updated',
];

/* ── Create Webhook Modal ── */

interface CreateWebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CreateWebhookModal({ isOpen, onClose }: CreateWebhookModalProps): JSX.Element {
  const { addToast } = useToast();
  const createMutation = useCreateWebhook();
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [createdSecrets, setCreatedSecrets] = useState<Array<{ eventType: string; secret: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleEvent = (event: string): void => {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  };

  const handleClose = (): void => {
    setUrl('');
    setSelectedEvents([]);
    setCreatedSecrets([]);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!url.trim()) {
      addToast('Informe a URL do webhook', 'error');
      return;
    }
    if (selectedEvents.length === 0) {
      addToast('Selecione ao menos um evento', 'error');
      return;
    }
    // Contrato real: 1 assinatura por eventType → N POSTs sequenciais.
    setIsSubmitting(true);
    const secrets: Array<{ eventType: string; secret: string }> = [];
    try {
      for (const eventType of selectedEvents) {
        const created = await createMutation.mutateAsync({ url: url.trim(), eventType });
        if (created.secret) secrets.push({ eventType, secret: created.secret });
      }
      setCreatedSecrets(secrets);
      addToast(
        secrets.length > 1
          ? `${secrets.length} assinaturas criadas`
          : 'Webhook criado com sucesso',
        'success',
      );
      setUrl('');
      setSelectedEvents([]);
    } catch (err) {
      addToast((err as Error).message ?? 'Erro ao criar webhook', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Novo Webhook"
      size="md"
      footer={
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outline" onClick={handleClose}>
            {createdSecrets.length > 0 ? 'Fechar' : 'Cancelar'}
          </Button>
          {createdSecrets.length === 0 && (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Criando...' : 'Criar'}
            </Button>
          )}
        </div>
      }
    >
      <div className="create-webhook-form">
        {createdSecrets.length > 0 ? (
          <div role="alert" style={{ padding: '1rem', background: '#ffddba', border: '1px solid #f2bc82', borderRadius: '4px' }}>
            <p style={{ margin: '0 0 0.5rem', fontWeight: 700, fontSize: '0.875rem', color: '#633f0f' }}>
              Guarde os segredos agora — exibidos uma única vez
            </p>
            {createdSecrets.map((s) => (
              <p key={s.eventType} style={{ margin: '0.25rem 0', fontSize: '0.8125rem', color: '#191c1e', wordBreak: 'break-all' }}>
                <strong>{s.eventType}:</strong> {s.secret}
              </p>
            ))}
          </div>
        ) : (
        <>
        <Input
          label="URL do Webhook"
          placeholder="https://example.com/webhook"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />

        <div style={{ marginTop: '1rem' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#191c1e',
              marginBottom: '0.5rem',
            }}
          >
            Eventos (uma assinatura por tipo)
          </label>
          <div className="events-grid">
            {AVAILABLE_EVENTS.map((event) => (
              <label key={event} className="event-checkbox">
                <input
                  type="checkbox"
                  checked={selectedEvents.includes(event)}
                  onChange={() => handleToggleEvent(event)}
                />
                <span>{event}</span>
              </label>
            ))}
          </div>
        </div>
        </>
        )}
      </div>

      <style>{`
        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 0.5rem;
        }
        .event-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
          color: #191c1e;
          cursor: pointer;
          padding: 0.375rem 0.5rem;
          border-radius: 4px;
          transition: background 0.15s;
        }
        .event-checkbox:hover {
          background: #f2f4f6;
        }
      `}</style>
    </Modal>
  );
}

/* ── Deliveries Modal ── */

interface DeliveriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhookId: string;
  webhookUrl: string;
}

function DeliveriesModal({
  isOpen,
  onClose,
  webhookId,
  webhookUrl,
}: DeliveriesModalProps): JSX.Element {
  const { data, isLoading, error } = useWebhookDeliveries(webhookId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Entregas - ${webhookUrl}`}
      size="lg"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {isLoading ? (
        <LoadingSkeleton count={4} height="32px" />
      ) : error ? (
        <p style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>
          Erro ao carregar entregas.
        </p>
      ) : !data || data.length === 0 ? (
        <p style={{ color: '#43474e', fontSize: '0.875rem' }}>
          Nenhuma entrega registrada para este webhook.
        </p>
      ) : (
        <div className="deliveries-table">
          <div className="deliveries-header">
            <span>Data</span>
            <span>Evento</span>
            <span>Status</span>
            <span>HTTP</span>
            <span>Resposta</span>
          </div>
          {data.map((d: Delivery) => (
            <div key={d.id} className="deliveries-row">
              <span>{formatDate(d.createdAt)}</span>
              <span>{d.event}</span>
              <span>
                <Badge
                  variant={
                    d.status === 'success'
                      ? 'success'
                      : d.status === 'failed'
                        ? 'error'
                        : 'neutral'
                  }
                >
                  {d.status}
                </Badge>
              </span>
              <span>{d.httpStatus}</span>
              <span
                title={d.responseBody}
                style={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {d.responseBody || '-'}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .deliveries-table {
          font-size: 0.8125rem;
        }
        .deliveries-header,
        .deliveries-row {
          display: grid;
          grid-template-columns: 140px 140px 80px 60px 1fr;
          gap: 0.5rem;
          padding: 0.5rem 0;
          align-items: center;
        }
        .deliveries-header {
          font-weight: 600;
          color: #43474e;
          border-bottom: 1px solid #e0e3e5;
          padding-bottom: 0.5rem;
          margin-bottom: 0.25rem;
        }
        .deliveries-row {
          border-bottom: 1px solid #eceef0;
        }
        .deliveries-row:last-child {
          border-bottom: none;
        }
      `}</style>
    </Modal>
  );
}

/* ── Main Page ── */

export function IntegrationWebhooksPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: webhooks, isLoading, error } = useWebhooks();
  const deleteMutation = useDeleteWebhook();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deliveriesModal, setDeliveriesModal] = useState<{
    id: string;
    url: string;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Webhook removido com sucesso', 'success');
        setConfirmDelete(null);
      },
      onError: (err) => {
        addToast((err as Error).message ?? 'Erro ao remover webhook', 'error');
      },
    });
  };

  return (
    <div className="integration-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Webhooks</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Webhooks registrados e suas entregas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button onClick={() => setShowCreateModal(true)}>Novo Webhook</Button>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <LoadingSkeleton count={4} height="48px" />
          </div>
        ) : error ? (
          <div className="state-message error">
            <p>Erro ao carregar webhooks.</p>
          </div>
        ) : !webhooks || webhooks.length === 0 ? (
          <div className="state-message empty">
            <p>Nenhum webhook configurado.</p>
            <p style={{ fontSize: '0.8125rem', color: '#74777f' }}>
              Crie um webhook para receber notificacoes de eventos do sistema.
            </p>
          </div>
        ) : (
          <div className="webhooks-list">
            <div className="list-header">
              <span className="col-url">URL</span>
              <span className="col-events">Eventos</span>
              <span className="col-status">Status</span>
              <span className="col-created">Criado em</span>
              <span className="col-actions">Ações</span>
            </div>
            {webhooks.map((wh: Webhook) => (
              <div key={wh.id} className="list-row">
                <span className="col-url" title={wh.url}>
                  {wh.url}
                </span>
                <span className="col-events">
                  <Badge variant="info">
                    {wh.eventType}
                  </Badge>
                </span>
                <span className="col-status">
                  {/* Tres estados, como o dominio: pausado e desativado nao
                      sao a mesma coisa para quem depende da entrega. */}
                  <Badge variant={WEBHOOK_STATUS_VARIANT[wh.status]} dot>
                    {WEBHOOK_STATUS_LABEL[wh.status]}
                  </Badge>
                </span>
                <span className="col-created">{formatDate(wh.createdAt)}</span>
                <span className="col-actions">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDeliveriesModal({ id: wh.id, url: wh.url })
                    }
                  >
                    Entregas
                  </Button>
                  {confirmDelete === wh.id ? (
                    <div className="confirm-delete">
                      <span>Tem certeza?</span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(wh.id)}
                        disabled={deleteMutation.isPending}
                      >
                        Sim
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Não
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDelete(wh.id)}
                    >
                      Excluir
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <CreateWebhookModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {deliveriesModal && (
        <DeliveriesModal
          isOpen={!!deliveriesModal}
          onClose={() => setDeliveriesModal(null)}
          webhookId={deliveriesModal.id}
          webhookUrl={deliveriesModal.url}
        />
      )}

      <style>{`
        .integration-page { padding: 1.5rem; }

        .state-message {
          padding: 2rem;
          text-align: center;
        }
        .state-message p { margin: 0.25rem 0; font-size: 0.875rem; color: #43474e; }
        .state-message.error p { color: #ba1a1a; }

        .webhooks-list {
          font-size: 0.8125rem;
        }
        .list-header,
        .list-row {
          display: grid;
          grid-template-columns: 2fr 2fr 100px 130px 1fr;
          gap: 1rem;
          padding: 0.75rem 1rem;
          align-items: center;
        }
        .list-header {
          background: #f2f4f6;
          font-weight: 600;
          color: #43474e;
          border-bottom: 1px solid #e0e3e5;
        }
        .list-row {
          border-bottom: 1px solid #eceef0;
          transition: background 0.15s;
        }
        .list-row:hover {
          background: #f2f4f6;
        }
        .col-url {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .col-events {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
        }
        .col-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .confirm-delete {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.75rem;
          color: #ba1a1a;
        }
      `}</style>
    </div>
  );
}
